/**
 * Referral Tracking Page
 *
 * Displays referral earnings and relationships for guides:
 * - Referral commission summary (base bonus, revenue share, specialty bounty)
 * - Active referral relationships
 * - Referral earnings history
 * - Performance metrics per referred guide
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-17: Referral Tracking Display (3 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/dashboard/referrals
 * - Uses DashboardMetricsRepository.getReferralMetrics()
 * - Uses ReferralService for referral relationships
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  DollarSign,
  Users as _Users,
  TrendingUp as _TrendingUp,
  Award,
  Calendar,
  AlertCircle as _AlertCircle,
  ExternalLink,
  Star as _Star,
  Users,
  TrendingUp
} from 'lucide-react';
import { formatCentsToDollars } from '../../utils/formHelpers';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Avatar from '../../components/common/Avatar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import StatusBadge from '../../components/common/StatusBadge';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import { getGuidePhotoUrl } from '../../utils/guideDataAdapters';

export default function ReferralTracking() {
  const navigate = useNavigate();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch referral data
  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/api/v1/referrals/overview', { method: 'GET' });
      // Map backend response shape to component's expected structure
      setReferralData({
        total_referrals: data.summary?.totalReferrals || 0,
        active_referrals: data.summary?.activatedCount || 0,
        total_earnings: data.summary?.totalPaidCents || 0,
        this_month_earnings: 0,
        base_bonus_total: 0,
        revenue_share_total: 0,
        specialty_bounty_total: 0,
        referred_guides: (data.recentReferrals || []).map(r => ({
          id: r.id,
          name: r.referred_name,
          email: r.referred_email,
          status: r.referral_status,
          referred_at: r.referred_at,
          total_payout_cents: r.total_payout_cents,
        })),
        recent_payouts: data.topReferrers || [],
      });
    } catch (err) {
      console.error('Error fetching referral data:', err);
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load referral data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Map commission type to StatusBadge variant
  const getCommissionVariant = (type) => {
    switch (type) {
      case 'BASE_BONUS':
        return 'success';
      case 'REVENUE_SHARE':
        return 'info';
      case 'SPECIALTY_BOUNTY':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  // Get commission type label
  const getCommissionTypeLabel = (type) => {
    switch (type) {
      case 'BASE_BONUS':
        return 'Base Bonus';
      case 'REVENUE_SHARE':
        return 'Revenue Share';
      case 'SPECIALTY_BOUNTY':
        return 'Specialty Bounty';
      default:
        return type;
    }
  };

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="guide" activeNavId="earnings" onNavigate={handleNavigate}>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner message="Loading referral data..." />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="guide" activeNavId="earnings" onNavigate={handleNavigate}>
        <Alert variant="error" message={error} />
      </DashboardLayout>
    );
  }

  const {
    total_referrals = 0,
    active_referrals = 0,
    total_earnings = 0,
    this_month_earnings = 0,
    base_bonus_total = 0,
    revenue_share_total = 0,
    specialty_bounty_total = 0,
    referred_guides = [],
    recent_payouts = []
  } = referralData || {};

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="earnings"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Referral Tracking</h1>
            <p className="mt-1 text-sm text-gray-500">
              Track your referral earnings and partner network
            </p>
          </div>
          <button
            onClick={() => handleNavigate('earnings', '/guide/earnings')}
            className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <DollarSign className="w-5 h-5" />
            <span>Back to Earnings</span>
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total Referrals */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Referrals</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {total_referrals}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {active_referrals} active
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          {/* This Month Earnings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {formatCurrency(this_month_earnings)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Referral commissions</p>
              </div>
              <Calendar className="w-8 h-8 text-green-600" />
            </div>
          </div>

          {/* Total Earnings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">All-Time Earnings</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCentsToDollars(total_earnings)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Total commissions</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          {/* Avg per Referral */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg per Referral</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCentsToDollars(total_referrals > 0 ? Math.round(total_earnings / total_referrals) : 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Lifetime value</p>
              </div>
              <Award className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Commission Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Commission Breakdown</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Base Bonus */}
              <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Base Bonus</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(base_bonus_total)}</p>
                  <p className="text-xs text-gray-600 mt-1">One-time signup bonuses</p>
                </div>
              </div>

              {/* Revenue Share */}
              <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Revenue Share</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(revenue_share_total)}</p>
                  <p className="text-xs text-gray-600 mt-1">Ongoing commission (2-5%)</p>
                </div>
              </div>

              {/* Specialty Bounty */}
              <div className="flex items-center space-x-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Specialty Bounty</p>
                  <p className="text-2xl font-bold text-purple-600">{formatCurrency(specialty_bounty_total)}</p>
                  <p className="text-xs text-gray-600 mt-1">High-demand specialty bonuses</p>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <Alert variant="info" className="mt-6">
              <p className="font-medium mb-1">How Referral Commissions Work</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>Base Bonus:</strong> $500 one-time payment when referred guide completes first booking</li>
                <li><strong>Revenue Share:</strong> 2-5% of referred guide&apos;s booking revenue (ongoing)</li>
                <li><strong>Specialty Bounty:</strong> $250-$1,000 bonus for referring guides with high-demand specialties</li>
              </ul>
            </Alert>
          </div>
        </div>

        {/* Referred Guides */}
        {referred_guides && referred_guides.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Referred Guides</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {referred_guides.map((guide) => (
                  <div key={guide.guide_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    {/* Guide Info */}
                    <div className="flex items-center space-x-4 flex-1">
                      <Avatar
                        src={getGuidePhotoUrl(guide, 'profile', 'thumbnail')}
                        name={guide.guide_name}
                        size="lg"
                        className="flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {guide.guide_name}
                        </p>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs text-gray-500">
                            Joined {formatDate(guide.referred_at)}
                          </span>
                          {guide.is_active && (
                            <StatusBadge variant="success">Active</StatusBadge>
                          )}
                          {guide.specialty && (
                            <Badge color="purple">{guide.specialty}</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Earnings from this Guide */}
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(guide.total_commissions || 0)}
                        </p>
                        <p className="text-xs text-gray-500">Total earned</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-700">
                          {guide.booking_count || 0}
                        </p>
                        <p className="text-xs text-gray-500">Bookings</p>
                      </div>
                      <button
                        onClick={() => handleNavigate('guide-profile', `/guides/${guide.guide_id}`)}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="View guide profile"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recent Payouts */}
        {recent_payouts && recent_payouts.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Payouts</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referred Guide
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recent_payouts.map((payout) => (
                    <tr key={payout.payout_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payout.payout_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payout.referred_guide_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge variant={getCommissionVariant(payout.payout_type)}>
                          {getCommissionTypeLabel(payout.payout_type)}
                        </StatusBadge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {payout.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                        {formatCurrency(payout.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!referred_guides || referred_guides.length === 0) && (
          <EmptyState
            icon={Users}
            title="No Referrals Yet"
            description="Start earning commission by referring guides to the platform. You'll earn bonuses and ongoing revenue share."
            action={
              <button className="inline-flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">
                <Users className="w-5 h-5" />
                <span>Invite a Guide</span>
              </button>
            }
          />
        )}
      </div>
    </DashboardLayout>
  );
}
